import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Reply, MessageCircle, MoreVertical, Send, Trash2 } from "lucide-react";
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase, getProfileWithLogging, trackCommentForChallenge } from '@/integrations/supabase/client';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogClose 
} from "@/components/ui/dialog";
import { triggerCommentAdd } from "@/utils/onboardingEvents";
import CommentLikeButton from "@/components/CommentLikeButton";
import { detectSpecialAdminEmails } from '@/utils/adminUtils';

interface Comment {
  id: string;
  content: string;
  user_id: string;
  recipe_id: string;
  created_at: string;
  parent_comment_id?: string;
  userProfile?: {
    id: string;
    name: string;
    username?: string;
    avatar_url?: string;
    points?: number;
  };
}

interface RecipeCommentsProps {
  recipeId: string;
}

const RecipeComments: React.FC<RecipeCommentsProps> = ({ recipeId }) => {
  const navigate = useNavigate();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<{ commentId: string | null, parentId: string | null }>({ 
    commentId: null, 
    parentId: null 
  });
  const [editMode, setEditMode] = useState<string | null>(null);
  const [editedComment, setEditedComment] = useState('');
  const [showAllComments, setShowAllComments] = useState(false);
  const [commentsToShow, setCommentsToShow] = useState(3);
  const { user } = useAuth();
  const { toast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [profiles, setProfiles] = useState<{ [userId: string]: any }>({});
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user) {
      // Check if the user is an admin
      const checkAdminStatus = async () => {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();

          const isSpecialAdmin = user.email ? detectSpecialAdminEmails(user.email) : false;
          setIsAdmin((data?.role === 'admin') || isSpecialAdmin);
        } catch (err) {
          console.error("Error checking admin status:", err);
        }
      };
      
      checkAdminStatus();
    }
  }, [user]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (user) {
        await fetchComments();
      } else {
        setLoading(false);
      }
    };

    loadInitialData();
    
    const debugToast = () => {
      console.log("Toast was triggered - debugging popup source");
    };
    
    return () => {
    };
  }, [user, recipeId]);

  useEffect(() => {
    if (comments.length > commentsToShow) {
      setShowAllComments(false);
    } else {
      setShowAllComments(true);
    }
  }, [comments, commentsToShow]);

  const fetchComments = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('recipe_comments')
        .select(`
          id, 
          content, 
          user_id, 
          recipe_id, 
          created_at, 
          parent_comment_id
        `)
        .eq('recipe_id', recipeId)
        .order('created_at', { ascending: false });

      if (error) {
        setError(error.message);
        console.error("Error fetching comments:", error);
        setLoading(false);
        return;
      }

      const commentsWithProfiles: Comment[] = await Promise.all(
        data.map(async (comment) => {
          try {
            const profile = await getProfileWithLogging(comment.user_id);
            
            return {
              ...comment,
              userProfile: {
                id: profile?.id || comment.user_id,
                name: profile?.name || 'Usuario',
                username: profile?.username,
                avatar_url: profile?.avatar_url,
                points: profile?.points || 0
              }
            };
          } catch (err) {
            console.error(`Error fetching profile for comment ${comment.id}:`, err);
            return {
              ...comment,
              userProfile: {
                id: comment.user_id,
                name: 'Usuario',
                username: undefined,
                avatar_url: undefined,
                points: 0
              }
            };
          }
        })
      );

      const fetchedProfiles: { [userId: string]: any } = {};

      commentsWithProfiles.forEach(comment => {
        if (comment.userProfile) {
          fetchedProfiles[comment.user_id] = comment.userProfile;
        }
      });

      setComments(commentsWithProfiles);
      setProfiles(fetchedProfiles);
    } catch (err) {
      setError("Failed to load comments.");
      console.error("Error in fetchComments:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = (username?: string) => {
    if (!username) {
      console.log("No username provided for navigation");
      return;
    }
    
    console.log(`Navigating to user profile: ${username}`);
    navigate(`/usuario/${username}`);
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    if (!user) {
      setLoginDialogOpen(true);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('recipe_comments')
        .insert([
          {
            content: newComment,
            user_id: user.id,
            recipe_id: recipeId,
            parent_comment_id: replyingTo.commentId || null
          }
        ])
        .select('id, content, user_id, recipe_id, created_at, parent_comment_id')
        .single();

      if (error) {
        console.error("Error adding comment:", error);
        toast({
          description: "No se pudo añadir el comentario. Intenta de nuevo.",
          notificationType: "error"
        });
      } else {
        setNewComment('');
        
        let userProfile;
        
        if (user.profile && typeof user.profile === 'object' && 'name' in user.profile) {
          userProfile = user.profile;
        } else {
          userProfile = await getProfileWithLogging(user.id);
        }
        
        const newCommentWithProfile: Comment = {
          ...data,
          userProfile: {
            id: user.id,
            name: typeof userProfile?.name === 'string' ? userProfile.name : 'Usuario',
            username: typeof userProfile?.username === 'string' ? userProfile.username : undefined,
            avatar_url: typeof userProfile?.avatar_url === 'string' ? userProfile.avatar_url : undefined,
            points: typeof userProfile?.points === 'number' ? userProfile.points : 0
          }
        };
        
        setComments(prevComments => [newCommentWithProfile, ...prevComments]);
        
        const timestamp = new Date().toISOString();
        const commentData = { 
          recipeId, 
          commentId: data.id,
          timestamp
        };
        
        if (user.id) {
          console.log("Tracking comment for challenge:", commentData);
          trackCommentForChallenge(user.id, commentData);
          
          triggerCommentAdd();
          
          console.log("Comment event dispatched:", commentData);
        }

        toast({
          description: "Comentario añadido correctamente.",
          notificationType: "success"
        });
        setReplyingTo({ commentId: null, parentId: null });
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        description: "No se pudo añadir el comentario. Intenta de nuevo.",
        notificationType: "error"
      });
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      // If the user is an admin, they can delete any comment
      // Otherwise, they can only delete their own comments
      const { error } = await supabase
        .from('recipe_comments')
        .delete()
        .eq('id', commentId)
        .or(isAdmin ? `id.eq.${commentId}` : `user_id.eq.${user?.id}`);

      if (error) {
        console.error("Error deleting comment:", error);
        toast({
          description: "No se pudo eliminar el comentario. Intenta de nuevo.",
          notificationType: "error"
        });
      } else {
        setComments(comments.filter(comment => comment.id !== commentId));
        toast({
          description: "Comentario eliminado correctamente.",
          notificationType: "success"
        });
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        description: "No se pudo eliminar el comentario. Intenta de nuevo.",
        notificationType: "error"
      });
    }
  };

  const updateComment = async (commentId: string, newContent: string) => {
    try {
      const { data, error } = await supabase
        .from('recipe_comments')
        .update({ content: newContent })
        .eq('id', commentId)
        .select('*')
        .single();

      if (error) {
        console.error("Error updating comment:", error);
        toast({
          description: "No se pudo actualizar el comentario. Intenta de nuevo.",
          notificationType: "error"
        });
      } else {
        setComments(comments.map(comment =>
          comment.id === commentId ? { ...comment, content: newContent } : comment
        ));
        setEditMode(null);
        setEditedComment('');
        toast({
          description: "Comentario actualizado correctamente.",
          notificationType: "success"
        });
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      toast({
        description: "No se pudo actualizar el comentario. Intenta de nuevo.",
        notificationType: "error"
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewComment(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const getCommentsToDisplay = () => {
    const topLevelComments = comments.filter(comment => !comment.parent_comment_id);
    
    const result: Comment[] = [];
    
    topLevelComments.forEach(parentComment => {
      result.push(parentComment);
      
      const replies = comments.filter(comment => 
        comment.parent_comment_id === parentComment.id
      );
      
      replies.sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ).forEach(reply => {
        result.push(reply);
      });
    });
    
    return result;
  };

  const handleReplyClick = (commentId: string, parentId: string | null = null) => {
    setReplyingTo({ commentId, parentId });
    setNewComment('');
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
  };

  const cancelReply = () => {
    setReplyingTo({ commentId: null, parentId: null });
    setNewComment('');
  };

  const renderCommentReplyBox = (parentComment: Comment) => {
    if (replyingTo.commentId === parentComment.id) {
      return (
        <div className="ml-8 mt-2 mb-2">
          <Textarea
            ref={textareaRef}
            placeholder="Escribe tu respuesta..."
            value={newComment}
            onChange={handleInputChange}
            className="resize-none border rounded-md focus:ring-0 focus:border-gray-300 text-sm"
          />
          <div className="flex justify-end mt-2 space-x-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={cancelReply}
            >
              Cancelar
            </Button>
            <Button 
              onClick={addComment} 
              size="sm"
            >
              Enviar <Send className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            {user && user.profile && typeof user.profile === 'object' && 
             'avatar_url' in user.profile && 'name' in user.profile ? (
              <Avatar className="h-8 w-8">
                <AvatarImage 
                  src={typeof user.profile.avatar_url === 'string' ? user.profile.avatar_url : ""} 
                  alt={typeof user.profile.name === 'string' ? user.profile.name : "Avatar"} 
                />
                <AvatarFallback>
                  {typeof user.profile.name === 'string' && user.profile.name.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
            ) : (
              <Avatar className="h-8 w-8">
                <AvatarFallback>U</AvatarFallback>
              </Avatar>
            )}
            <div className="flex-1 space-y-2">
              {replyingTo.commentId && (
                <div className="flex items-center text-sm text-muted-foreground mb-2">
                  <MessageCircle className="h-4 w-4 mr-1" />
                  <span>
                    Respondiendo a {comments.find(c => c.id === replyingTo.commentId)?.userProfile?.name || 'usuario'}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="ml-2 h-6 py-0 px-2 text-xs" 
                    onClick={cancelReply}
                  >
                    Cancelar
                  </Button>
                </div>
              )}
              <Textarea
                ref={textareaRef}
                placeholder={replyingTo.commentId ? "Escribe tu respuesta..." : "Añade un comentario..."}
                value={newComment}
                onChange={handleInputChange}
                className="resize-none border rounded-md focus:ring-0 focus:border-gray-300 text-sm"
              />
              <div className="flex justify-end">
                <Button onClick={addComment} size="sm">
                  Enviar <Send className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <p>Cargando comentarios...</p>}
      {error && <p className="text-red-500">Error: {error}</p>}

      {getCommentsToDisplay().map(comment => (
        <React.Fragment key={comment.id}>
          <Card 
            className={`mb-2 ${comment.parent_comment_id ? 'ml-8 border-l-4 border-l-primary/20' : ''}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage 
                    src={comment.userProfile?.avatar_url || ""} 
                    alt={comment.userProfile?.name || "Avatar"} 
                  />
                  <AvatarFallback>{typeof comment.userProfile?.name === 'string' ? comment.userProfile.name.charAt(0) : "U"}</AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-1">
                  <div className="flex items-baseline justify-between">
                    <div className="text-sm font-medium flex items-center gap-2">
                      <button 
                        onClick={() => handleProfileClick(comment.userProfile?.username)}
                        className="hover:underline text-left font-medium"
                      >
                        {comment.userProfile?.name}
                      </button>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-6 w-6 p-0 rounded-full">
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Abrir menú</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        {user?.id === comment.user_id ? (
                          <>
                            <DropdownMenuItem onClick={() => {
                              setEditMode(comment.id);
                              setEditedComment(comment.content);
                            }}>
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteComment(comment.id)}>
                              Eliminar
                            </DropdownMenuItem>
                          </>
                        ) : isAdmin ? (
                          <DropdownMenuItem onClick={() => deleteComment(comment.id)}>
                            <Trash2 className="mr-2 h-4 w-4 text-red-500" /> 
                            Eliminar
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem disabled>
                            Reportar
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {comment.parent_comment_id && (
                    <div className="text-xs text-muted-foreground mb-1">
                      <MessageCircle className="h-3 w-3 inline mr-1" />
                      Respuesta a {comments.find(c => c.id === comment.parent_comment_id)?.userProfile?.name || 'usuario'}
                    </div>
                  )}
                  {editMode === comment.id ? (
                    <div className="space-y-2">
                      <Input
                        type="text"
                        value={editedComment}
                        onChange={(e) => setEditedComment(e.target.value)}
                        className="border rounded-md focus:ring-0 focus:border-gray-300 text-sm"
                      />
                      <div className="flex justify-end space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditMode(null)}>
                          Cancelar
                        </Button>
                        <Button size="sm" onClick={() => updateComment(comment.id, editedComment)}>
                          Guardar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700">{comment.content}</p>
                  )}
                  <div className="text-xs text-gray-500">
                    Publicado {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: es })}
                  </div>
                  <div className="flex items-center space-x-4 text-xs">
                    <CommentLikeButton commentId={comment.id} size="sm" />
                    {!comment.parent_comment_id && (
                      <button 
                        className="flex items-center space-x-1" 
                        onClick={() => handleReplyClick(comment.id)}
                      >
                        <Reply className="h-4 w-4 text-gray-400" />
                        <span>Responder</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {renderCommentReplyBox(comment)}
        </React.Fragment>
      ))}

      {comments.length > commentsToShow && !showAllComments && (
        <Button variant="link" onClick={() => setCommentsToShow(comments.length)}>
          Mostrar todos los comentarios
        </Button>
      )}

      {replyingTo.commentId && (
        <Button variant="outline" onClick={cancelReply} className="mt-2">
          Cancelar respuesta
        </Button>
      )}

      <Dialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Inicia sesión</DialogTitle>
            <DialogDescription>
              Debes iniciar sesión para poder comentar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setLoginDialogOpen(false)}>
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecipeComments;
